/**
 * How a proposal reads: the current and the proposed text side by side with
 * the changed words marked (`DiffPanes`), or a list of what changed
 * (`ChangeList`). The marks are `<del>` and `<ins>` and each carries a
 * visually hidden "removed" or "added", so the change never rests on colour.
 */

import { Minus, Pencil, Plus } from 'lucide-react';
import { useMemo, type ReactNode } from 'react';

import { diffWords, type DiffPart } from './diff';

export interface DiffChange {
  kind: 'add' | 'change' | 'remove';
  text: string;
}

/** The words of one side of a diff: shared text as it is, the changed words in `<del>` or `<ins>` led by a hidden "removed: " or "added: ". */
export function markedWords(parts: DiffPart[], mark: 'del' | 'ins'): ReactNode[] {
  return parts
    .filter((part) => part.kind === 'same' || part.kind === mark)
    .map((part, index) => {
      if (part.kind === 'same') return part.text;
      const Tag = mark;
      return (
        <Tag key={index}>
          <span className="sr-only">{mark === 'del' ? 'removed: ' : 'added: '}</span>
          {part.text}
        </Tag>
      );
    });
}

function Pane({ title, tone, parts, mark }: { title: string; tone: 'before' | 'after'; parts: DiffPart[]; mark: 'del' | 'ins' }) {
  const body = markedWords(parts, mark);
  return (
    <div className="diff-pane" role="group" aria-label={title}>
      <div className={`diff-head ${tone}`}>{title}</div>
      <div className="refine-diff-body">{body.length ? body : <span className="hint">Empty</span>}</div>
    </div>
  );
}

/** `titles` names the two panes (a history diff reads Before and After). */
export function DiffPanes({
  current,
  proposed,
  titles = ['Current', 'Proposed'],
}: {
  current: string;
  proposed: string;
  titles?: [string, string];
}) {
  const parts = useMemo(() => diffWords(current, proposed), [current, proposed]);
  return (
    <div className="refine-diff">
      <Pane title={titles[0]} tone="before" parts={parts} mark="del" />
      <Pane title={titles[1]} tone="after" parts={parts} mark="ins" />
    </div>
  );
}

const KINDS = {
  add: { label: 'Added', pill: 'badge-success', Icon: Plus },
  change: { label: 'Changed', pill: 'badge-info', Icon: Pencil },
  remove: { label: 'Removed', pill: 'badge-danger', Icon: Minus },
} as const;

export function ChangeList({ changes }: { changes: DiffChange[] }) {
  return (
    <ul className="refine-changes" aria-label="Changes">
      {changes.map(({ kind, text }, index) => {
        const { label, pill, Icon } = KINDS[kind];
        return (
          <li key={index}>
            <span className={`badge ${pill}`}>
              <Icon aria-hidden="true" />
              {label}
            </span>
            <span>{text}</span>
          </li>
        );
      })}
    </ul>
  );
}
